/** Admin billing endpoints used by Billing → Plans & Prices and Coupons &
 *  Discounts. Mirrors backend/app/modules/subscriptions/admin_routes.py. */
import { apiClient } from "../../lib/http/apiClient";
import { parseApiError } from "../../lib/http/apiErrors";
import { adminHeaders } from "./service";

const BASE = "/api/admin/subscriptions";

// ── Plans & Prices ────────────────────────────────────────────────────

export type PlanCellStatus = "ready" | "missing" | "unpriced";

export interface PlanCell {
  cycle: "monthly" | "yearly";
  currency: "INR" | "USD";
  /** subscription_config key holding this price, e.g. PRICE_BUYER_PRO_YEARLY_USD */
  key: string;
  /** Whole units of `currency`. 0 = not offered. */
  price: number;
  plan_id: string | null;
  plan_name: string | null;
  status: PlanCellStatus;
}

export interface PlansMatrix {
  flags: Record<
    "YEARLY_PLANS_ENABLED" | "PLAN_CATALOG_ENABLED" | "DISCOUNTS_ENABLED" | "PRICES_EXCLUDE_GST" | "SUBSCRIPTION_CREDITS_ENABLED",
    number
  >;
  gateway: { cashfree_enabled: boolean; mode: string };
  rows: { role: "seller" | "buyer"; tier: string; cells: PlanCell[] }[];
}

export interface PlanSyncResult {
  plans?: { plan_key: string; cycle: string; currency: string; plan_id: string; name: string }[];
  errors?: { plan_key?: string; cycle?: string; currency?: string; error: string }[];
  skipped?: string;
}

export async function fetchPlansMatrix(): Promise<PlansMatrix> {
  const { data } = await apiClient.get<PlansMatrix>(`${BASE}/plans-matrix`, { headers: adminHeaders() });
  return data;
}

/** Save a config value (a price or a 0/1 switch). Prices also create their
 *  Cashfree plans; `message` says whether that worked. */
export async function setConfigValue(
  key: string,
  value: number,
): Promise<{ message: string; plan_sync?: PlanSyncResult }> {
  const { data } = await apiClient.put(`${BASE}/config/${key}`, { value }, { headers: adminHeaders() });
  return data;
}

export async function syncAllPlans(): Promise<PlanSyncResult> {
  const { data } = await apiClient.post<PlanSyncResult>(
    `${BASE}/payment-gateway/plan-catalog/sync`, {}, { headers: adminHeaders() },
  );
  return data;
}

// ── Coupons & Discounts ───────────────────────────────────────────────

export interface DiscountAudience {
  kind: "everyone" | "users" | "segment";
  user_ids: string[];
  roles: string[];
  signed_up_after: string | null;
  new_subscribers_only: boolean;
}

export interface DiscountAppliesTo {
  roles: string[];
  tiers: string[];
  cycles: ("monthly" | "yearly")[];
}

export interface DiscountInput {
  name: string;
  code: string | null;
  type: "percent" | "flat";
  value: number;
  duration: "first_payment" | "every_renewal";
  audience: DiscountAudience;
  applies_to: DiscountAppliesTo;
  starts_at: string | null;
  ends_at: string | null;
  max_redemptions: number | null;
  per_user_limit: number | null;
  active: boolean;
  /** Show in the checkout "Available coupons" list (needs a code). */
  listed?: boolean;
  /** Full terms, one per line. Empty = generated defaults. */
  terms?: string | null;
}

export interface Discount extends DiscountInput {
  id: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  /** Confirmed redemptions plus live reservations. */
  used?: number;
}

export interface Redemption {
  user_id: string;
  code: string | null;
  plan_key: string;
  cycle: string;
  list_amount_minor: number;
  charged_amount_minor: number;
  status: "reserved" | "confirmed" | "released";
  created_at: string;
}

export async function fetchDiscounts(): Promise<{ enabled: boolean; discounts: Discount[] }> {
  const { data } = await apiClient.get(`${BASE}/discounts`, { headers: adminHeaders() });
  return data;
}

export async function previewDiscountTerms(body: DiscountInput): Promise<{ generated: string[]; effective: string[] }> {
  const { data } = await apiClient.post(`${BASE}/discounts/terms-preview`, body, { headers: adminHeaders() });
  return data;
}

export async function createDiscount(body: DiscountInput): Promise<Discount & { plan_sync?: PlanSyncResult }> {
  const { data } = await apiClient.post(`${BASE}/discounts`, body, { headers: adminHeaders() });
  return data;
}

export async function updateDiscount(
  id: string,
  body: DiscountInput,
): Promise<Discount & { plan_sync?: PlanSyncResult }> {
  const { data } = await apiClient.put(`${BASE}/discounts/${id}`, body, { headers: adminHeaders() });
  return data;
}

export async function deactivateDiscount(id: string): Promise<Discount> {
  const { data } = await apiClient.delete(`${BASE}/discounts/${id}`, { headers: adminHeaders() });
  return data;
}

/** Entries may be user ids or email addresses. */
export async function addDiscountUsers(
  id: string,
  refs: string[],
): Promise<Discount & { not_found: string[] }> {
  const { data } = await apiClient.post(`${BASE}/discounts/${id}/users`, { user_ids: refs }, { headers: adminHeaders() });
  return data;
}

export async function fetchRedemptions(id: string): Promise<Redemption[]> {
  const { data } = await apiClient.get(`${BASE}/discounts/${id}/redemptions`, { headers: adminHeaders() });
  return data.redemptions;
}

// ── Invoices ───────────────────────────────────────────────────────────

const INVOICES_BASE = "/api/admin/invoices";

export interface InvoiceSettings {
  enabled: boolean;
  enabled_at: string | null;
  legal_name: string;
  trade_name: string;
  gstin: string;
  pan: string;
  address: string;
  state_code: string;
  email: string;
  sac_code: string;
  gst_rate_pct: number;
  lut_number: string;
  series_prefix: string;
  receipt_prefix: string;
  min_amount_minor: number;
  email_enabled: boolean;
  whatsapp_enabled: boolean;
  whatsapp_template: string;
  whatsapp_template_language: string;
  link_ttl_days: number;
  email_attach_pdfs: boolean;
  whatsapp_attach_pdf: boolean;
  reminders_enabled: boolean;
  reminder_days_yearly: number[];
  reminder_days_monthly: number[];
  whatsapp_template_renewal: string;
  whatsapp_template_failed: string;
  whatsapp_template_lapsing: string;
}

/** PUT body: any subset of the settings above, except `enabled_at` (server-set). */
export type InvoiceSettingsInput = Partial<Omit<InvoiceSettings, "enabled_at">>;

export interface InvoiceSettingsResponse {
  settings: InvoiceSettings;
  /** e.g. { "07": "Delhi" } — for the state_code <select>. */
  state_codes: Record<string, string>;
}

export type InvoiceKind = "subscription" | "upgrade" | "topup";
export type InvoiceStatus = "issued" | "void";

export type InvoiceDeliveryChannel = "email" | "whatsapp";
export type InvoiceDeliveryStatus = "queued" | "sending" | "sent" | "skipped" | "failed" | "pending";

export interface InvoiceDeliveryAttempt {
  status: InvoiceDeliveryStatus;
  at: string | null;
  error: string | null;
}

export interface Invoice {
  id: string;
  invoice_no: string;
  receipt_no: string;
  kind: InvoiceKind;
  status: InvoiceStatus;
  issued_at: string;
  description: string;
  currency: string;
  total_minor: number;
  tax_minor: number;
  tax_type: string;
  buyer_name: string;
  buyer_gstin: string | null;
  buyer_email: string;
  user_id: string;
  void_reason: string | null;
  /** Relative signed path, valid 1h, no auth needed — prefix with the API base URL. */
  invoice_url: string;
  /** Same as invoice_url, for the receipt PDF. */
  receipt_url: string;
  /** Per-channel delivery attempt, absent when that channel was never queued. */
  delivery?: {
    email?: InvoiceDeliveryAttempt;
    whatsapp?: InvoiceDeliveryAttempt;
  };
}

export interface InvoiceListParams {
  q?: string;
  kind?: InvoiceKind;
  status?: InvoiceStatus;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export interface InvoiceListResponse {
  total: number;
  page: number;
  limit: number;
  invoices: Invoice[];
}

export async function fetchInvoiceSettings(): Promise<InvoiceSettingsResponse> {
  const { data } = await apiClient.get<InvoiceSettingsResponse>(`${INVOICES_BASE}/settings`, { headers: adminHeaders() });
  return data;
}

export async function updateInvoiceSettings(body: InvoiceSettingsInput): Promise<{ settings: InvoiceSettings }> {
  const { data } = await apiClient.put<{ settings: InvoiceSettings }>(`${INVOICES_BASE}/settings`, body, { headers: adminHeaders() });
  return data;
}

export async function fetchInvoices(params: InvoiceListParams = {}): Promise<InvoiceListResponse> {
  const { data } = await apiClient.get<InvoiceListResponse>(INVOICES_BASE, { params, headers: adminHeaders() });
  return data;
}

/** Fetches the CSV as a blob (the export endpoint needs the admin auth header,
 *  which a plain <a href> can't attach) and triggers a browser save. Filename
 *  is read from the response's Content-Disposition when the server sends one. */
export async function exportInvoicesCsv(params: InvoiceListParams = {}): Promise<void> {
  const response = await apiClient.get(`${INVOICES_BASE}/export.csv`, {
    params, headers: adminHeaders(), responseType: "blob",
  });
  const disposition: string | undefined = response.headers?.["content-disposition"];
  const match = disposition ? /filename\*?=(?:UTF-8''|")?([^";]+)"?/i.exec(disposition) : null;
  const filename = match ? decodeURIComponent(match[1]) : "invoices.csv";
  const blob = new Blob([response.data], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function voidInvoice(id: string, reason: string): Promise<{ ok: boolean }> {
  const { data } = await apiClient.post<{ ok: boolean }>(`${INVOICES_BASE}/${id}/void`, { reason }, { headers: adminHeaders() });
  return data;
}

/** Queues a resend on the given channels. `queued` echoes back only the
 *  channels actually queued — a channel already queued/sent or unavailable
 *  (e.g. WhatsApp for a customer who never opted in) is silently omitted. */
export async function resendInvoice(
  id: string,
  channels: InvoiceDeliveryChannel[],
): Promise<{ queued: string[] }> {
  const { data } = await apiClient.post<{ queued: string[] }>(
    `${INVOICES_BASE}/${id}/resend`, { channels }, { headers: adminHeaders() },
  );
  return data;
}

// ── Invoice design (templates) ──────────────────────────────────────────

export type TemplateDocKey = "invoice" | "receipt";

export interface InvoiceTemplateDoc {
  active_version: number | null;
  html: string;
  is_default: boolean;
}

export interface TemplatePlaceholder {
  placeholder: string;
  description: string;
}

export interface InvoiceTemplatesResponse {
  docs: { invoice: InvoiceTemplateDoc; receipt: InvoiceTemplateDoc };
  default_html: string;
  variables: TemplatePlaceholder[];
  max_bytes: number;
}

export interface InvoiceTemplateVersion {
  version: number;
  note: string;
  created_by: string;
  created_at: string;
  active: boolean;
  html: string;
}

export interface InvoiceTemplateVersionsResponse {
  versions: InvoiceTemplateVersion[];
}

/** Thrown by the save/preview/activate calls below on a 400 (syntax error,
 *  render failure, or a missing GST-required field) — `errors` is the raw
 *  list from `detail.errors`, `message` is the same list joined for toasts. */
export interface TemplateValidationError extends Error {
  errors: string[];
}

function extractTemplateErrors(err: unknown): string[] {
  const errors = (err as { response?: { data?: { detail?: { errors?: unknown } } } })?.response?.data?.detail?.errors;
  return Array.isArray(errors) ? errors.filter((e): e is string => typeof e === "string") : [];
}

function toTemplateError(err: unknown): TemplateValidationError {
  const errors = extractTemplateErrors(err);
  const message = errors.length ? errors.join(" ") : parseApiError(err).message;
  const templateError = new Error(message) as TemplateValidationError;
  templateError.errors = errors;
  return templateError;
}

function parseHeaderJsonArray(raw: unknown): string[] {
  if (typeof raw !== "string" || !raw.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

export async function fetchInvoiceTemplates(): Promise<InvoiceTemplatesResponse> {
  const { data } = await apiClient.get<InvoiceTemplatesResponse>(`${INVOICES_BASE}/templates`, { headers: adminHeaders() });
  return data;
}

export async function fetchInvoiceTemplateVersions(doc: TemplateDocKey): Promise<InvoiceTemplateVersionsResponse> {
  const { data } = await apiClient.get<InvoiceTemplateVersionsResponse>(
    `${INVOICES_BASE}/templates/${doc}/versions`, { headers: adminHeaders() },
  );
  return data;
}

/** Renders a PDF from unsaved HTML — the PDF comes back even when GST fields
 *  are missing (`errors`) or only soft issues are found (`warnings`); only a
 *  syntax/render failure rejects (thrown as a TemplateValidationError). */
export async function previewInvoiceTemplate(
  doc: TemplateDocKey,
  body: { html: string; invoice_id?: string },
): Promise<{ blob: Blob; errors: string[]; warnings: string[] }> {
  try {
    const response = await apiClient.post(`${INVOICES_BASE}/templates/${doc}/preview`, body, {
      headers: adminHeaders(),
      responseType: "blob",
    });
    return {
      blob: response.data as Blob,
      errors: parseHeaderJsonArray(response.headers?.["x-template-errors"]),
      warnings: parseHeaderJsonArray(response.headers?.["x-template-warnings"]),
    };
  } catch (err) {
    // responseType: "blob" means even a 400's JSON body lands as a Blob —
    // read it back to text so toTemplateError can find detail.errors.
    const response = (err as { response?: { data?: unknown } })?.response;
    if (response?.data instanceof Blob) {
      try {
        response.data = JSON.parse(await response.data.text());
      } catch {
        // leave as-is; toTemplateError falls back to a generic message
      }
    }
    throw toTemplateError(err);
  }
}

export async function saveInvoiceTemplate(
  doc: TemplateDocKey,
  body: { html: string; note?: string },
): Promise<{ saved: InvoiceTemplateVersion }> {
  try {
    const { data } = await apiClient.post<{ saved: InvoiceTemplateVersion }>(
      `${INVOICES_BASE}/templates/${doc}`, body, { headers: adminHeaders() },
    );
    return data;
  } catch (err) {
    throw toTemplateError(err);
  }
}

export async function activateInvoiceTemplateVersion(
  doc: TemplateDocKey,
  version: number,
): Promise<{ active: number }> {
  try {
    const { data } = await apiClient.post<{ active: number }>(
      `${INVOICES_BASE}/templates/${doc}/versions/${version}/activate`, {}, { headers: adminHeaders() },
    );
    return data;
  } catch (err) {
    throw toTemplateError(err);
  }
}

export async function resetInvoiceTemplate(doc: TemplateDocKey): Promise<{ ok: boolean }> {
  const { data } = await apiClient.post<{ ok: boolean }>(`${INVOICES_BASE}/templates/${doc}/reset`, {}, { headers: adminHeaders() });
  return data;
}

// ── Invoice logo (used as {{ brand.logo }} on invoice + receipt PDFs) ──

export interface InvoiceLogo {
  custom: boolean;
  data_uri: string;
  width?: number;
  height?: number;
  original_filename?: string;
  uploaded_by?: string;
  uploaded_at?: string;
}

export async function fetchInvoiceLogo(): Promise<InvoiceLogo> {
  const { data } = await apiClient.get<InvoiceLogo>(`${INVOICES_BASE}/logo`, { headers: adminHeaders() });
  return data;
}

export async function uploadInvoiceLogo(file: File): Promise<InvoiceLogo> {
  const form = new FormData();
  form.append("file", file);
  const { data } = await apiClient.post<InvoiceLogo>(`${INVOICES_BASE}/logo`, form, { headers: adminHeaders() });
  return data;
}

export async function resetInvoiceLogo(): Promise<InvoiceLogo> {
  const { data } = await apiClient.delete<InvoiceLogo>(`${INVOICES_BASE}/logo`, { headers: adminHeaders() });
  return data;
}
